import React, { useCallback } from 'react';
import type { ApolloError } from 'apollo-client';
import { useMutation } from '@apollo/react-hooks';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import { R400 } from '@atlaskit/theme/colors';
import { useFlags } from '@atlaskit/flag';

import { StarMutation } from './StarMutation.graphql';
import { UnstarMutation } from './UnstarMutation.graphql';
import type {
	StarMutation as StarMutationType,
	StarMutationVariables,
} from './__types__/StarMutation';
import type {
	UnstarMutation as UnstarMutationType,
	UnstarMutationVariables,
} from './__types__/UnstarMutation';

const i18n = defineMessages({
	starMutationErrorTitle: {
		id: 'feed.card.star.mutation.error.title',
		defaultMessage: 'We’re having trouble starring that page',
		description:
			'Title of error flag shown when a user attempts to star the feed card but the action fails.',
	},
	unstarMutationErrorTitle: {
		id: 'feed.card.unstar.mutation.error.title',
		defaultMessage: 'We’re having trouble unstarring that page',
		description:
			'Title of error flag shown when a user attempts to unstar the feed card but the action fails.',
	},
	starMutationErrorDescription: {
		id: 'feed.card.star.mutation.error.description',
		defaultMessage: 'Try it again.',
		description:
			'Body of error flag shown when a user attempts to star/unstar the feed card but the action fails. Shown below the error title',
	},
});

type UseContentStarResponse = [(starred: boolean) => Promise<void>, ApolloError | undefined];

export function useContentStar(contentId: string): UseContentStarResponse {
	const { showFlag } = useFlags();
	const onMutationError = useCallback(
		(isStarred: boolean) => () => {
			showFlag({
				description: <FormattedMessage {...i18n.starMutationErrorDescription} />,
				title: (
					<FormattedMessage
						{...(isStarred ? i18n.unstarMutationErrorTitle : i18n.starMutationErrorTitle)}
					/>
				),
				icon: (
					<ErrorIcon label="error" color={token('color.icon.danger', R400)} spacing="spacious" />
				),
				isAutoDismiss: true,
			});
		},
		[showFlag],
	);

	const [mutateStar, { error: starError }] = useMutation<StarMutationType, StarMutationVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		StarMutation,
		{
			variables: { contentId },
			optimisticResponse: {
				favouritePage: {
					__typename: 'FavouritePagePayload',
					content: {
						__typename: 'Content',
						id: contentId,
						metadata: {
							__typename: 'ContentMetadata',
							currentuser: {
								__typename: 'ContentMetadata_CurrentUserMetadataProvider_currentuser',
								favourited: {
									// @ts-ignore TS doesn't recognize __typename which is needed for mutation
									__typename: 'FavouritedSummary',
									isFavourite: true,
								},
							},
						},
					},
				},
			},
			onError: onMutationError(false),
		},
	);

	const [mutateUnstar, { error: unstarError }] = useMutation<
		UnstarMutationType,
		UnstarMutationVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		UnstarMutation,
		{
			variables: { contentId },
			optimisticResponse: {
				unfavouritePage: {
					__typename: 'UnfavouritePagePayload',
					content: {
						__typename: 'Content',
						id: contentId,
						metadata: {
							__typename: 'ContentMetadata',
							currentuser: {
								// @ts-ignore TS doesn't recognize __typename which is needed for mutation
								__typename: 'ContentMetadata_CurrentUserMetadataProvider_currentuser',
								favourited: null,
							},
						},
					},
				},
			},
			onError: onMutationError(true),
		},
	);

	const toggleStarState = useCallback(
		async (starred: boolean) => {
			try {
				if (starred) {
					await mutateUnstar();
				} else {
					await mutateStar();
				}
			} catch (_) {} //errors handled via useMutation
		},
		[mutateStar, mutateUnstar],
	);

	return [toggleStarState, starError || unstarError];
}
